import * as fabricTypes from 'fabric'
import { fabric } from '~/utils/editor/fabric'
import { AnyObjectState, ObjectMode, ObjectType } from '~/static/enums/object-type'
import { guid } from '~/utils/Helpers'
import { setControls } from '~/utils/editor/helpers/ControlsHelper'
import { ActualVersion, ActualVersionNumber } from '~/utils/editor/versioning/object'
import { calculateZoom } from './helpers/Helpers'
import { Rgba } from '../ColorpickerHelper'
import { AnyActualProp } from './versioning/migrators'
import { Action } from '~/static/types/editor'

export const DefaultEditorWidth = 900
export const DefaultEditorHeight = 1200

export type FabricObject = fabricTypes.FabricObject & { refObject: EditorObject }

export abstract class ObjectState {
  onScaling?: (object: EditorObject) => void
  onMoving?: (object: EditorObject) => void
  onAfterSet?: (object: EditorObject) => void

  abstract onAdd(object: EditorObject): void
  abstract onRemove(object: EditorObject): void

  needRemoveOnFocusout(): boolean {
    return true
  }
}

export interface LayoutContext {
  width: number
  height: number
  zoom: number
  lastFontFamily?: string
}

export interface NamingContext {
  getNextIndex: (type: ObjectType) => number
}

export abstract class EditorObject {
  id!: string
  type: ObjectType

  blocked: boolean = false
  hidden: boolean = false
  createdAt: number

  left: number = 0
  top: number = 0
  angle: number = 0
  flipX: boolean = false
  flipY: boolean = false

  object!: FabricObject
  states: { [key: string]: ObjectState } = {}

  preview: string | null = null

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructor(type: ObjectType) {
    this.type = type
    this.createdAt = Date.now()
    this.resetId()
  }

  setId(id: string) {
    this.id = id
  }
  resetId() {
    this.id = guid()
  }

  protected setObject(object: fabricTypes.Object) {
    this.object = Object.assign(object, { refObject: this })
    this.setControls()
  }

  protected assignProperties(props: ActualVersion | object) {
    Object.keys(props).forEach((prop) => {
      const propsKey = <keyof typeof props>prop
      const thisKey = <keyof typeof this>prop
      if (
        props[propsKey] !== undefined &&
        ![ 'data', 'object', 'zoom', 'type', 'states', 'bodyColour' ].includes(prop)
      ) {
        this[thisKey] = props[propsKey]
      }
    })
  }

  abstract load(mode: ObjectMode): Promise<void>

  abstract applyInitialLayout(context: LayoutContext): void

  abstract applyDefaultName(context: NamingContext): void

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  abstract set(props: ActualVersion | object): Promise<void>

  protected applyProps(): Promise<void> {
    this.object.set({
      dirty: true,
      left: this.left - (this.object.group?.left ?? 0) - (this.object.group?.width ?? 0) / 2,
      top: this.top - (this.object.group?.top ?? 0) - (this.object.group?.height ?? 0) / 2,
      angle: this.angle - (this.object.group?.angle ?? 0),
      flipX: this.flipX,
      flipY: this.flipY,
      selectable: !(this.blocked || this.hidden),
      evented: !(this.blocked || this.hidden),
      opacity: this.hidden ? 0 : undefined,
    })

    for (const state of Object.values(this.states)) {
      if (state.onAfterSet) {
        state.onAfterSet(this)
      }
    }
    return Promise.resolve()
  }

  draw(): Promise<void> {
    return this.applyProps()
  }

  toObject(): ActualVersion {
    const data: { [ key: string ]: unknown } = {
      version: ActualVersionNumber,
      title: this.name,
      bodyColour: this.bodyColour,
    }

    Object.keys(this).forEach((prop) => {
      const thisKey = <keyof typeof this>prop
      if (this[thisKey] !== undefined && ![ 'data', 'object', 'zoom', 'states', 'title' ].includes(prop)) {
        data[prop] = this[thisKey]
      }
    })
  
    data.states = Object.keys(this.states)

    return data
  }

  toJson(): string {
    const data = this.toObject()
    delete data.preview
    delete data.bodyColour
    return JSON.stringify(data)
  }

  addState(key: string, state: ObjectState) {
    if (key in this.states) {
      return
    }

    this.states[key] = state
    this.states[key].onAdd(this)
  }

  removeState(key: string) {
    if (key in this.states) {
      const state = this.states[key]
      delete this.states[key]
      state.onRemove(this)
    }
  }

  clearStates() {
    for (const key in this.states) {
      if (this.states[key].needRemoveOnFocusout()) {
        const state = this.states[key]
        delete this.states[key]
        state.onRemove(this)
      }
    }
  }

  getStates(): string[] {
    return Object.keys(this.states)
  }

  onSelect() {}

  onUnselect() {
    this.clearStates()
  }

  onChangeFocusWithoutUnselection() {
    this.clearStates()
  }

  onDoubleClick() {}

  afterModified(): boolean {
    this.setCanvasManagedProperties()
    return false
  }

  onMoving(): boolean {
    for (const state of Object.values(this.states)) {
      if (state.onMoving) {
        state.onMoving(this)
      }
    }
    return false
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onScaling(_event: fabricTypes.ModifiedEvent): boolean {
    for (const state of Object.values(this.states)) {
      if (state.onScaling) {
        state.onScaling(this)
      }
    }
    return false
  }

  onRotating(): boolean {
    return false
  }

  afterScaling(): boolean {
    return false
  }

  get freezeEditor(): boolean {
    return false
  }

  get name(): string {
    switch (this.type) {
    case ObjectType.Icon:
      return 'Иконка'
    case ObjectType.Text:
      return 'Текстetg'
    case ObjectType.SvgFigure:
    case ObjectType.Line:
      return 'Фигура'
    default:
      return ''
    }
  }

  get canBeSelected(): boolean {
    return !this.blocked && !this.hidden
  }

  abstract get bodyColour(): Rgba

  getRealObjectWidth() {
    return (this.object.scaleX ?? 1) * (this.object.group?.scaleX ?? 1) * (<number>this.object.width)
  }

  getRealObjectHeight() {
    return (this.object.scaleY ?? 1) * (this.object.group?.scaleY ?? 1) * (<number>this.object.height)
  }

  setCanvasManagedProperties() {
    const left = this.object.left ?? 0
    const top = this.object.top ?? 0
    if (this.object.group !== undefined) {
      const bluredMatrix = this.object.group.calcTransformMatrix()
      const angleRadians = -(this.object.group.angle ?? 0) * (Math.PI / 180)
      const rotatePoint = new fabric.Point(bluredMatrix[4], bluredMatrix[5])

      const ltControlGroupRotated = this.object.group.getPointByOrigin('left', 'top')
      const ltControlGroup = fabric.util.rotatePoint(ltControlGroupRotated, rotatePoint, angleRadians)

      const ltControlObject = new fabric.Point(
        ((this.object.group.width ?? 0) / 2 + left) * (this.object.group.scaleX ?? 1) + ltControlGroup.x,
        ((this.object.group.height ?? 0) / 2 + top) * (this.object.group.scaleY ?? 1) + ltControlGroup.y,
      )
      const ltControlObjectRotated = fabric.util.rotatePoint(ltControlObject, rotatePoint, -angleRadians)

      this.left = ltControlObjectRotated.x
      this.top = ltControlObjectRotated.y

      this.flipX = (this.object.flipX ?? false) !== (this.object.group.flipX ?? false)
      this.flipY = (this.object.flipY ?? false) !== (this.object.group.flipX ?? false)
      this.angle = ((this.object.angle ?? 0) + (this.object.group.angle ?? 0)) % 360
    } else {
      this.left = left
      this.top = top
      this.flipX = (this.object.flipX ?? false)
      this.flipY = (this.object.flipY ?? false)
      this.angle = (this.object.angle ?? 0) % 360
    }
  }

  getCanvasManagedProperties(): AnyActualProp {
    return {
      left: this.left,
      top: this.top,
      angle: this.angle,
      flipX: this.flipX,
      flipY: this.flipY,
    }
  }

  setControls() {
    setControls(this.object)
  }

  async updatePreview() {
    // 48x40, but for retina it is x2
    const previewWidth = 96
    const previewHeight = 80

    const object = await this.object.clone()
    object.set({ hidden: false, opacity: 1, shadow: undefined })
    if (object.type === 'group') {
      (object as unknown as fabricTypes.Group).forEachObject((child) => {
        child.set({ hidden: false, opacity: 1, shadow: undefined })
      })
    }

    const boundingBox = object.getBoundingRect()
    const canvas = new fabric.StaticCanvas(undefined, {
      width: boundingBox.width,
      height: boundingBox.height,
    })
    canvas.add(object)
    canvas.centerObject(object)

    const scalefactor = calculateZoom(boundingBox.width, boundingBox.height, previewWidth, previewHeight)
    canvas.setZoom(scalefactor)

    canvas.renderAll()

    const blob = await canvas.toBlob({
      width: boundingBox.width * scalefactor,
      height: boundingBox.height * scalefactor,
      left: 0,
      top: 0,
      format: 'png',
      multiplier: 1,
    })
    this.preview = blob ? URL.createObjectURL(blob) : null
  }
}

export class RotateState extends ObjectState {
  angle: number

  constructor(angle: number) {
    super()
    this.angle = angle
  }

  onAdd(object: EditorObject): void {
    const oldProps = object.getCanvasManagedProperties()
    object.angle = this.angle
    object.object.rotate(this.angle)
    object.object.setCoords()
    object.removeState(AnyObjectState.Rotate)
    object.afterModified()
    object.object.canvas?.fire('object:modified', {
      action: Action.CustomRotate,
      target: object.object,
      modification: [ {
        id: object.id,
        oldProps: oldProps,
        newProps: object.getCanvasManagedProperties(),
      } ],
    })
  }

  onRemove(): void {}
}
